import type {
  IPayoutVerificationChallenge,
  IWalletCashOut,
  IWalletCashOutDetail,
  IWalletCashOutOptions,
  IWalletCashOutQuoteResult,
  IWalletCashOutRequest,
  IWalletCashOutResult,
} from '@share/common-types';
import { axiosInstance, getHeaders } from '@share/utils';
import { Urls } from '@share/constants';

// The wallet code comes from the logged member's wallet (loginStore), never from user input; it is
// still encoded. Amount and product go as axios params.
const cashOutPath = (walletCode: string) =>
  `${Urls.Wallet}/${encodeURIComponent(walletCode)}/cashout`;

export const getWalletCashOutOptionsAsync = (walletCode: string) =>
  axiosInstance.get<IWalletCashOutOptions>(`${cashOutPath(walletCode)}/options`, {
    ...getHeaders(false),
  });

export const quoteWalletCashOutAsync = (walletCode: string, amount: number, productId: string) =>
  axiosInstance.get<IWalletCashOutQuoteResult>(`${cashOutPath(walletCode)}/quote`, {
    ...getHeaders(false),
    params: { amount, productId },
  });

export const createWalletCashOutAsync = (
  walletCode: string,
  body: IWalletCashOutRequest,
  idempotencyKey: string,
) =>
  axiosInstance.post<IWalletCashOutResult>(cashOutPath(walletCode), body, {
    ...getHeaders(false),
    headers: { ...getHeaders(false).headers, 'Idempotency-Key': idempotencyKey },
  });

export const getWalletCashOutsAsync = (walletCode: string) =>
  axiosInstance.get<IWalletCashOut[]>(cashOutPath(walletCode), { ...getHeaders(false) });

export const getWalletCashOutAsync = (walletCode: string, cashOutCode: string) =>
  axiosInstance.get<IWalletCashOutDetail>(
    `${cashOutPath(walletCode)}/${encodeURIComponent(cashOutCode)}`,
    { ...getHeaders(false) },
  );

/** Emails the member the code that confirms a cash-out, when the options require one. */
export const sendWalletCashOutVerificationAsync = () =>
  axiosInstance.post<IPayoutVerificationChallenge>(
    Urls.PayoutVerification,
    { purpose: 'AgentCommissionCashOut' },
    { ...getHeaders(false) },
  );

/** The reason Payments gave, when there is one; otherwise the fallback. */
export const getWalletCashOutErrorMessage = (error: any, fallback: string): string => {
  let data = error?.response?.data;

  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return data || fallback;
    }
  }

  return data?.failureReason ?? data?.reason ?? data?.message ?? fallback;
};
